import { AvailableLocale } from '@omnea-digital/toolbox'
import { useQuery } from '@tanstack/react-query'
import { createContext, PropsWithChildren, useMemo, useContext } from 'react'
import { I18nextProvider } from 'react-i18next'
import { DateFormatKey } from 'utils/date/dateFormat'
import { usePotentiallyAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useGetUserByEmail } from '@omnea-app/fe-users-and-groups/useGetUserByEmail'
import { loadingComponent } from '@omnea-app/frontend/components/shared/Loading/loadingComponent'
import { determineDateFormat, determineLocale, LocaleSource } from './determineLocaleAndDateFormat'
import { createI18nInstanceWithFallbackResources, getTranslationsJson } from './getFrontendI18n'

interface TranslationsContext {
  locale: AvailableLocale
  localeSource: LocaleSource
  datePickerFormat: DateFormatKey
  datePickerFormatSource: LocaleSource
}

const TranslationsContext = createContext<TranslationsContext>({
  get locale(): AvailableLocale {
    throw new Error('Translations context not yet initialized')
  },
  get localeSource(): LocaleSource {
    throw new Error('Translations context not yet initialized')
  },
  get datePickerFormat(): DateFormatKey {
    throw new Error('Translations context not yet initialized')
  },
  get datePickerFormatSource(): LocaleSource {
    throw new Error('Translations context not yet initialized')
  },
})

export const useTranslationsContext = () => {
  return useContext(TranslationsContext)
}

export const TranslationsProvider: React.FC<PropsWithChildren> = loadingComponent(
  ({ children }) => {
    const { user } = usePotentiallyAuthenticatedUser()
    const { data: userData } = useGetUserByEmail(user?.email)

    const contextValue = useMemo<
      Pick<TranslationsContext, 'locale' | 'localeSource' | 'datePickerFormat' | 'datePickerFormatSource'>
    >(
      () => ({
        ...determineLocale(userData),
        ...determineDateFormat(userData),
      }),
      [userData]
    )

    const i18nData = useQuery({
      queryKey: ['translations-json', contextValue.locale],
      queryFn: () => getTranslationsJson(contextValue.locale),
    }).data

    const i18nInstance = useMemo(() => {
      return createI18nInstanceWithFallbackResources(contextValue.locale, i18nData)
    }, [contextValue.locale, i18nData])

    return (
      <TranslationsContext.Provider value={contextValue}>
        <I18nextProvider i18n={i18nInstance} defaultNS="common">
          {children}
        </I18nextProvider>
      </TranslationsContext.Provider>
    )
  },
  {
    catchError: false,
  }
)

interface MockTranslationsProviderProps extends PropsWithChildren {
  locale?: AvailableLocale
}

export const MockTranslationsProvider: React.FC<MockTranslationsProviderProps> = ({ children, locale = 'en-GB' }) => {
  const contextValue = useMemo<TranslationsContext>(() => {
    return {
      locale,
      localeSource: 'browserLocale',
      datePickerFormat: 'DMY',
      datePickerFormatSource: 'browserLocale',
    }
  }, [locale])

  const i18nInstance = useMemo(() => {
    return createI18nInstanceWithFallbackResources(contextValue.locale, {})
  }, [contextValue.locale])

  return (
    <TranslationsContext.Provider value={contextValue}>
      <I18nextProvider i18n={i18nInstance} defaultNS="common">
        {children}
      </I18nextProvider>
    </TranslationsContext.Provider>
  )
}
